import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Chip, Alert,
  CircularProgress, InputAdornment, IconButton, Select, MenuItem,
  FormControl, InputLabel, Tooltip, Divider,
} from '@mui/material';
import {
  Login as LoginIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as OkIcon,
  Cancel as FailIcon,
  Clear as ClearIcon,
  Shield as AdminIcon,
  Person as StudentIcon,
  PhoneAndroid as TwoFAIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// ── Event type metadata ───────────────────────────────────────────────────────
const EVENT_META = {
  login_success:       { label: 'Connexion réussie',         color: '#16a34a', bg: '#dcfce7', icon: <OkIcon sx={{ fontSize: 14 }} /> },
  login_failed:        { label: 'Échec connexion',           color: '#dc2626', bg: '#fee2e2', icon: <FailIcon sx={{ fontSize: 14 }} /> },
  login_2fa_required:  { label: '2FA requis',                color: '#d97706', bg: '#fef3c7', icon: <TwoFAIcon sx={{ fontSize: 14 }} /> },
  login_2fa_failed:    { label: '2FA invalide',              color: '#dc2626', bg: '#fee2e2', icon: <TwoFAIcon sx={{ fontSize: 14 }} /> },
  admin_login_success: { label: 'Admin — Connexion réussie', color: '#2563eb', bg: '#dbeafe', icon: <AdminIcon sx={{ fontSize: 14 }} /> },
  admin_login_failed:  { label: 'Admin — Échec',            color: '#dc2626', bg: '#fee2e2', icon: <AdminIcon sx={{ fontSize: 14 }} /> },
  admin_2fa_required:  { label: 'Admin — 2FA requis',        color: '#d97706', bg: '#fef3c7', icon: <TwoFAIcon sx={{ fontSize: 14 }} /> },
  admin_2fa_failed:    { label: 'Admin — 2FA invalide',      color: '#dc2626', bg: '#fee2e2', icon: <TwoFAIcon sx={{ fontSize: 14 }} /> },
};

const EventChip = ({ type }) => {
  const meta = EVENT_META[type] || { label: type, color: '#64748b', bg: '#f1f5f9', icon: null };
  return (
    <Chip
      icon={meta.icon}
      label={meta.label}
      size="small"
      sx={{ height: 22, borderRadius: 1.5, fontWeight: 700, fontSize: '0.68rem', bgcolor: meta.bg, color: meta.color }}
    />
  );
};

// ── Stats bar ─────────────────────────────────────────────────────────────────
const StatsBar = ({ logs }) => {
  const total    = logs.length;
  const success  = logs.filter(l => l.success).length;
  const failed   = logs.filter(l => !l.success).length;
  const students = logs.filter(l => l.user_type === 'student').length;
  const admins   = logs.filter(l => l.user_type === 'admin').length;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {[
        { label: 'Total',     value: total,    color: '#475569', bg: '#f1f5f9' },
        { label: 'Réussies',  value: success,  color: '#16a34a', bg: '#dcfce7' },
        { label: 'Échouées',  value: failed,   color: '#dc2626', bg: '#fee2e2' },
        { label: 'Étudiants', value: students, color: '#2563eb', bg: '#dbeafe' },
        { label: 'Admins',    value: admins,   color: '#7c3aed', bg: '#ede9fe' },
      ].map(s => (
        <Paper key={s.label} elevation={0} sx={{ borderRadius: 2.5, border: `1.5px solid ${s.color}30`, px: 2.5, py: 1.5, minWidth: 100, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
        </Paper>
      ))}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AdminAuthLogs = () => {
  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [userType, setUserType]   = useState('');
  const [eventType, setEventType] = useState('');
  const [success, setSuccess]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { limit: 300 };
      if (search.trim()) params.search    = search.trim();
      if (userType)      params.user_type = userType;
      if (eventType)     params.event_type = eventType;
      if (success !== '')params.success   = success;
      const data = await adminAPI.getAuthLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [search, userType, eventType, success]);

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const clearFilters = () => {
    setSearch(''); setUserType(''); setEventType(''); setSuccess('');
    setTimeout(load, 0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoginIcon sx={{ fontSize: 26 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>Journal de Connexion</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>Historique des tentatives de connexion étudiants et admins</Typography>
        </Box>
        <Chip label={`${total} entrée(s)`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
      </Box>

      {/* Stats */}
      {!loading && logs.length > 0 && <StatsBar logs={logs} />}

      {/* Filters */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2.5, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              size="small" label="Rechercher (CIN / identifiant / nom)" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment>,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Utilisateur</InputLabel>
              <Select value={userType} onChange={e => setUserType(e.target.value)} label="Utilisateur">
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="student">Étudiants</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Type d'événement</InputLabel>
              <Select value={eventType} onChange={e => setEventType(e.target.value)} label="Type d'événement">
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(EVENT_META).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Résultat</InputLabel>
              <Select value={success} onChange={e => setSuccess(e.target.value)} label="Résultat">
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Réussies</MenuItem>
                <MenuItem value="false">Échouées</MenuItem>
              </Select>
            </FormControl>
            <Button type="submit" variant="contained" startIcon={<SearchIcon />} sx={{ borderRadius: 2, boxShadow: 'none' }}>
              Filtrer
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { clearFilters(); load(); }} sx={{ borderRadius: 2 }}>
              Réinitialiser
            </Button>
          </Box>
        </form>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 155 }}>Date & heure</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Événement</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Identifiant</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Raison d'échec</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 110 }}>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.disabled' }}>Aucune entrée trouvée</TableCell></TableRow>
            ) : logs.map(log => (
              <TableRow key={log.id} sx={{
                bgcolor: log.success ? 'white' : '#fff8f8',
                '&:hover': { bgcolor: log.success ? '#fafafa' : '#fff0f0' },
                borderLeft: `3px solid ${log.success ? '#dcfce7' : '#fee2e2'}`,
              }}>
                <TableCell sx={{ py: 1.2, fontSize: '0.75rem', color: '#475569', fontFamily: 'monospace' }}>
                  {new Date(log.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
                </TableCell>
                <TableCell sx={{ py: 1.2 }}>
                  <EventChip type={log.event_type} />
                </TableCell>
                <TableCell sx={{ py: 1.2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {log.user_type === 'admin'
                      ? <AdminIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
                      : <StudentIcon sx={{ fontSize: 14, color: '#2563eb' }} />}
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {log.identifier || '—'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.2 }}>
                  <Typography variant="body2" fontWeight={log.full_name ? 500 : 400} color={log.full_name ? 'text.primary' : 'text.disabled'}>
                    {log.full_name || '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.2 }}>
                  {log.failure_reason ? (
                    <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>
                      {log.failure_reason}
                    </Typography>
                  ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell sx={{ py: 1.2, fontSize: '0.72rem', color: '#64748b', fontFamily: 'monospace' }}>
                  <Tooltip title={log.user_agent || ''} placement="left">
                    <span>{log.ip_address || '—'}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2, mt: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={1}>Légende des événements</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(EVENT_META).map(([k, v]) => (
            <Chip key={k} icon={v.icon} label={v.label} size="small"
              sx={{ height: 20, borderRadius: 1, fontWeight: 600, fontSize: '0.65rem', bgcolor: v.bg, color: v.color }} />
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminAuthLogs;
